import { registerBlockType } from '@wordpress/blocks';
import {
	InnerBlocks,
	InspectorControls,
	useBlockProps,
	MediaUpload,
	MediaUploadCheck,
} from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	RangeControl,
	ToggleControl,
	TextControl,
	Button,
} from '@wordpress/components';
import './editor.css';

/* ---------- accordion template (root locked; interiors free) ------ */
/* ‼  No lock/templateLock attributes on the inner groups.
      They remain ordinary Group blocks so you can add / move / delete
      anything INSIDE them. */
const TEMPLATE = [
	[
		'core/group',
		{
			className: 'crispycohd-accordion-header',
			layout:    { type: 'flex', orientation: 'horizontal', justifyContent: 'space-between' },
		},
		[],
	],
	[
		'core/separator',
		{
			className: 'crispycohd-accordion-divider',
		},
	],
	[
		'core/group',
		{
			className: 'crispycohd-accordion-content',
			layout:    { type: 'flex', orientation: 'vertical' },
		},
		[],
	],
];

registerBlockType('crispycohd/accordion', {
	edit({ attributes, setAttributes }) {
		const { animation, duration, iconType, iconSvg, iconImgUrl, showDivider } = attributes;

		const blockProps = useBlockProps({
			'data-animation':  animation,
			'data-duration':   duration,
			'data-show-divider': showDivider,
		});

		return (
			<>
				<InspectorControls>
					{/* Animation */}
					<PanelBody title="Animation">
						<SelectControl
							label="Effect"
							value={animation}
							options={[
								{ label: 'Slide', value: 'slide' },
								{ label: 'Fade',  value: 'fade'  },
							]}
							onChange={(v) => setAttributes({ animation: v })}
						/>
						<RangeControl
							label="Duration (ms)"
							value={duration}
							min={100}
							max={2000}
							onChange={(v) => setAttributes({ duration: v })}
						/>
					</PanelBody>

					{/* Caret Icon */}
					<PanelBody title="Caret Icon" initialOpen={false}>
						<SelectControl
							label="Icon Source"
							value={iconType}
							options={[
								{ label: 'Built-in Caret', value: 'default' },
								{ label: 'Custom SVG',     value: 'svg'     },
								{ label: 'Custom Image',   value: 'image'   },
							]}
							onChange={(v) => setAttributes({ iconType: v })}
						/>
						{iconType === 'svg' && (
							<TextControl
								label="SVG Mark-up"
								value={iconSvg}
								onChange={(v) => setAttributes({ iconSvg: v })}
							/>
						)}
						{iconType === 'image' && (
							<MediaUploadCheck>
								<MediaUpload
									onSelect={(m) => setAttributes({ iconImgUrl: m.url })}
									allowedTypes={['image']}
									render={({ open }) => (
										<Button variant="secondary" onClick={open}>
											{iconImgUrl ? 'Replace Image' : 'Choose Image'}
										</Button>
									)}
								/>
							</MediaUploadCheck>
						)}
					</PanelBody>

					{/* Divider */}
					<PanelBody title="Divider" initialOpen={false}>
						<ToggleControl
							label="Show divider"
							checked={showDivider}
							onChange={(v) => setAttributes({ showDivider: v })}
						/>
					</PanelBody>
				</InspectorControls>

				<div {...blockProps}>
					{/* Root lock stops extra root blocks; renderAppender=false hides "+" */}
					<InnerBlocks
						template={TEMPLATE}
						renderAppender={false}
					/>
				</div>
			</>
		);
	},

	save({ attributes }) {
		const { animation, duration, iconType, iconSvg, iconImgUrl, showDivider } = attributes;

		const dataAttrs = {
			className:           `crispycohd-accordion crispycohd-accordion--${animation}`,
			'data-duration':     duration,
			'data-icon-type':    iconType,
			'data-show-divider': showDivider,
		};
		if (iconType === 'svg')   dataAttrs['data-icon-svg']  = iconSvg;
		if (iconType === 'image') dataAttrs['data-icon-img']  = iconImgUrl;

		return (
			<div {...useBlockProps.save(dataAttrs)}>
				<InnerBlocks.Content />
			</div>
		);
	},
});
